import {
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { Command } from "@/components/ui/command";
import { cn } from "@/lib/utils";
import {
  AccountMenuItem,
  MenuCheckbox,
  type SelectionState,
} from "./menu-parts";
import type { SelectorAccount, SelectorGroup } from "./types";
import type { SelectorSize } from "./types";

export interface AccountMultiSelectMenuProps {
  accounts: SelectorAccount[];
  groups?: SelectorGroup[];
  selectedIds: string[];
  onChange: (selectedIds: string[]) => void;
  searchable?: boolean;
  searchPlaceholder?: string;
  emptyMessage?: string;
  selectAllLabel?: string;
  ungroupedLabel?: string;
  showSelectAll?: boolean;
  size?: SelectorSize;
  className?: string;
  listClassName?: string;
}

type NormalizedGroup = SelectorGroup & { accounts: SelectorAccount[] };

function normalizeGroups(
  accounts: SelectorAccount[],
  groups: SelectorGroup[]
): { groups: NormalizedGroup[]; ungrouped: SelectorAccount[] } {
  const normalized: NormalizedGroup[] =
    groups.length > 0
      ? groups.map((group) => ({
          ...group,
          accounts:
            group.accounts ||
            accounts.filter(
              (account) =>
                account.groupId === group.id ||
                account.groupLabel === group.label
            ),
        }))
      : Array.from(
          new Set(accounts.map((account) => account.groupLabel).filter(Boolean))
        ).map((label) => ({
          id: label as string,
          label: label as string,
          accounts: accounts.filter((account) => account.groupLabel === label),
        }));

  const groupedIds = new Set(
    normalized.flatMap((group) => group.accounts.map((account) => account.id))
  );

  return {
    groups: normalized,
    ungrouped: accounts.filter((account) => !groupedIds.has(account.id)),
  };
}

function checkboxState(ids: string[], selected: Set<string>) {
  const selectedCount = ids.filter((id) => selected.has(id)).length;
  if (selectedCount === 0) return false;
  if (selectedCount === ids.length) return true;
  return "indeterminate" as const;
}

function ariaChecked(state: SelectionState) {
  return state === "indeterminate" ? ("mixed" as const) : state;
}

function MultiSelectMenuButton({
  checked,
  disabled,
  title,
  label,
  className,
  onClick,
  children,
}: {
  checked: SelectionState;
  disabled?: boolean;
  title?: string;
  label: string;
  className?: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role="menuitemcheckbox"
      aria-checked={ariaChecked(checked)}
      aria-label={label}
      disabled={disabled}
      data-disabled={disabled || undefined}
      title={title}
      onClick={onClick}
      className={cn(
        "relative flex w-full cursor-pointer items-center gap-2 rounded-none px-3.5 text-left font-inter text-sm font-medium text-neutral-800 outline-hidden select-none hover:bg-neutral-50 focus-visible:bg-neutral-50 focus-visible:text-neutral-900 disabled:pointer-events-none disabled:opacity-50",
        className
      )}
    >
      {children}
    </button>
  );
}

function MultiSelectSearchInput({
  value,
  onChange,
  placeholder,
  controls,
  size,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  controls: string;
  size: SelectorSize;
}) {
  return (
    <div
      className={cn(
        "flex items-center gap-2 border-b border-neutral-100",
        size === "md" ? "h-10 px-3.5" : "h-8 px-3"
      )}
    >
      <i
        aria-hidden
        className="fa-regular fa-magnifying-glass size-5 shrink-0 text-center text-[16px] leading-5 text-neutral-500"
      />
      <input
        type="text"
        role="searchbox"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Home" || event.key === "End") {
            event.stopPropagation();
          }
        }}
        aria-controls={controls}
        placeholder={placeholder}
        className="h-full w-full rounded-md bg-transparent py-0 font-inter text-sm outline-hidden placeholder:text-neutral-500"
      />
    </div>
  );
}

export function AccountMultiSelectMenu({
  accounts,
  groups = [],
  selectedIds,
  onChange,
  searchable = true,
  searchPlaceholder = "Search accounts...",
  emptyMessage = "No accounts found.",
  selectAllLabel = "Select all",
  ungroupedLabel = "Ungrouped",
  showSelectAll = true,
  size = "md",
  className,
  listClassName,
}: AccountMultiSelectMenuProps) {
  const [search, setSearch] = useState("");
  const listRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const normalized = useMemo(
    () => normalizeGroups(accounts, groups),
    [accounts, groups]
  );
  const query = search.trim().toLowerCase();
  const matches = (account: SelectorAccount) =>
    !query ||
    `${account.label} ${account.secondaryText || ""} ${account.searchText || ""}`
      .toLowerCase()
      .includes(query);
  const filteredGroups = normalized.groups
    .map((group) => ({
      ...group,
      accounts: group.accounts.filter(matches),
    }))
    .filter((group) => group.accounts.length > 0);
  const filteredUngrouped = normalized.ungrouped.filter(matches);
  const visibleAccounts = [
    ...filteredGroups.flatMap((group) => group.accounts),
    ...filteredUngrouped,
  ];
  const visibleIds = visibleAccounts
    .filter((account) => !account.disabled)
    .map((account) => account.id);
  const visibleSelectAllLabel = query
    ? `Select all ${visibleIds.length} results`
    : selectAllLabel;
  const selected = new Set(selectedIds);

  const toggleIds = (ids: string[]) => {
    const next = new Set(selectedIds);
    const allSelected = ids.length > 0 && ids.every((id) => next.has(id));
    ids.forEach((id) => (allSelected ? next.delete(id) : next.add(id)));
    onChange(Array.from(next));
  };

  const toggleAccount = (account: SelectorAccount) => {
    if (account.disabled) return;
    toggleIds([account.id]);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const current = (event.target as HTMLElement).closest<HTMLButtonElement>(
      '[role="menuitemcheckbox"]'
    );
    if (event.key === "Enter" && current) {
      event.preventDefault();
      event.stopPropagation();
      current.click();
      return;
    }

    const arrowKey = event.key === "ArrowDown" || event.key === "ArrowUp";
    const edgeKey = current && (event.key === "Home" || event.key === "End");
    if (!arrowKey && !edgeKey) return;

    const items = Array.from(
      listRef.current?.querySelectorAll<HTMLButtonElement>(
        '[role="menuitemcheckbox"]:not(:disabled)'
      ) || []
    );
    if (items.length === 0) return;

    event.preventDefault();
    event.stopPropagation();
    const currentIndex = current ? items.indexOf(current) : -1;
    const nextIndex =
      event.key === "Home"
        ? 0
        : event.key === "End"
          ? items.length - 1
          : event.key === "ArrowUp"
            ? currentIndex <= 0
              ? items.length - 1
              : currentIndex - 1
            : currentIndex >= items.length - 1
              ? 0
              : currentIndex + 1;
    items[nextIndex]?.focus();
  };

  return (
    <Command
      shouldFilter={false}
      label="Account selection"
      onKeyDown={handleKeyDown}
      className={cn(
        "h-auto rounded-lg border-neutral-100 py-1 shadow-[0_4px_8px_-2px_rgba(16,24,40,0.10),0_2px_4px_-2px_rgba(16,24,40,0.06)]",
        className
      )}
    >
      {searchable ? (
        <MultiSelectSearchInput
          value={search}
          onChange={setSearch}
          placeholder={searchPlaceholder}
          controls={listId}
          size={size}
        />
      ) : null}
      <div
        ref={listRef}
        id={listId}
        role="menu"
        aria-label="Accounts"
        tabIndex={searchable ? -1 : 0}
        className={cn(
          "max-h-[min(720px,var(--radix-popover-content-available-height,720px))] overflow-x-hidden overflow-y-auto",
          listClassName
        )}
      >
        {visibleAccounts.length === 0 ? (
          <div role="presentation" className="py-6 text-center text-sm">
            {emptyMessage}
          </div>
        ) : null}
        {showSelectAll && visibleIds.length > 0 ? (
          <div role="group" className="p-0">
            <MultiSelectMenuButton
              checked={checkboxState(visibleIds, selected)}
              label={visibleSelectAllLabel}
              onClick={() => toggleIds(visibleIds)}
              className={cn(size === "md" ? "h-10 py-2.5" : "h-8 py-1.5")}
            >
              <MenuCheckbox checked={checkboxState(visibleIds, selected)} />
              <span className="truncate">{visibleSelectAllLabel}</span>
            </MultiSelectMenuButton>
          </div>
        ) : null}
        {filteredGroups.map((group) => {
          const groupIds = group.accounts
            .filter((account) => !account.disabled)
            .map((account) => account.id);
          return (
            <div
              role="group"
              key={group.id}
              className={cn(
                "border-t border-neutral-100 p-0",
                size === "md" ? "pb-2.5" : "pb-1.5"
              )}
            >
              <MultiSelectMenuButton
                checked={checkboxState(groupIds, selected)}
                disabled={group.disabled || groupIds.length === 0}
                title={group.disabledReason}
                label={group.label}
                onClick={() => {
                  if (!group.disabled) toggleIds(groupIds);
                }}
                className={cn(size === "md" ? "h-10 py-2.5" : "h-8 py-1.5")}
              >
                <MenuCheckbox checked={checkboxState(groupIds, selected)} />
                <i
                  aria-hidden
                  className="fa-regular fa-share-nodes size-5 shrink-0 text-center text-[16px] leading-5 text-blue-500"
                />
                <span className="min-w-0 flex-1 truncate">{group.label}</span>
              </MultiSelectMenuButton>
              {group.accounts.map((account) => (
                <AccountMenuItem
                  key={account.id}
                  account={account}
                  multi
                  nested
                  size={size}
                  selected={selected.has(account.id)}
                  onSelect={() => toggleAccount(account)}
                />
              ))}
            </div>
          );
        })}
        {filteredUngrouped.length > 0 ? (
          <div
            role="group"
            className={cn(
              "border-t border-neutral-100 p-0",
              size === "md" ? "pb-2.5" : "pb-1.5"
            )}
          >
            <MultiSelectMenuButton
              checked={checkboxState(
                filteredUngrouped
                  .filter((account) => !account.disabled)
                  .map((account) => account.id),
                selected
              )}
              disabled={
                filteredUngrouped.every((account) => account.disabled) ||
                undefined
              }
              label={ungroupedLabel}
              onClick={() =>
                toggleIds(
                  filteredUngrouped
                    .filter((account) => !account.disabled)
                    .map((account) => account.id)
                )
              }
              className={cn(size === "md" ? "h-10 py-2.5" : "h-8 py-1.5")}
            >
              <MenuCheckbox
                checked={checkboxState(
                  filteredUngrouped
                    .filter((account) => !account.disabled)
                    .map((account) => account.id),
                  selected
                )}
              />
              <span className="min-w-0 flex-1 truncate">{ungroupedLabel}</span>
            </MultiSelectMenuButton>
            {filteredUngrouped.map((account) => (
              <AccountMenuItem
                key={account.id}
                account={account}
                multi
                nested
                size={size}
                selected={selected.has(account.id)}
                onSelect={() => toggleAccount(account)}
              />
            ))}
          </div>
        ) : null}
      </div>
    </Command>
  );
}
